'use client'

import { useNetworkChange } from '@/hooks/use-network-change'
import {
  getSessionAction,
  logoutAction,
  setSessionAction,
} from '@/lib/actions/auth'
import { mixpanelClient } from '@/lib/analytics/mixpanelClient'
import { Session, TOKEN_REFRESH_INTERVAL } from '@/lib/auth/auth-helper'
import { clearSessionData } from '@/lib/session/clear-session-data'
import { handleRefreshTokenResponse } from '@/lib/session/refresh-handler'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ModulusAPIResponse } from '@unx/shared/api/external-api'
import { getIsResponseUnauthorized } from '@unx/shared/api/utils'
import { refreshToken } from '@unx/shared/services/auth/refreshToken'
import { useTranslations } from 'next-intl'
import React, { ReactNode, useCallback, useEffect, useMemo } from 'react'
import { toast } from 'sonner'
import { useInterval } from 'usehooks-ts'

type RefreshTokenMutation = {
  accessToken?: string
}

const useRefreshTokenMutation = () => {
  return useMutation({
    mutationKey: ['session'],
    mutationFn: async (accessToken?: string) => {
      return refreshToken(accessToken ?? '')
    },
  })
}

const useSessionQuery = () => {
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const session = await getSessionAction()
      return session ?? null
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  })
}

interface SessionProvider {
  session: Session | null | undefined
  accessToken: string | undefined
  isAuthenticated: boolean
  openSession: (session: Session) => Promise<void>
  validateAPIResponse: (response: ModulusAPIResponse<unknown> | null) => void
  logout: (redirectPath?: string) => void
}

export const SessionContext = React.createContext<SessionProvider | undefined>(
  undefined,
)

interface Props {
  children: ReactNode
}

const SessionProvider = ({ children }: Props) => {
  const { data: session } = useSessionQuery()
  const t = useTranslations('generic.toast.unauthorized')
  const client = useQueryClient()

  const { mutate: refreshToken } = useRefreshTokenMutation()
  const userId = useMemo(() => session?.id, [session])

  // Identify user in Mixpanel on mount if session exists
  // Note: It's safe to call identify() multiple times with the same userId
  useEffect(() => {
    if (userId) {
      mixpanelClient.identify(userId)
    }
  }, [userId])

  const openSession = useCallback(
    async (newSession: Session) => {
      // Use server action to set session
      const result = await setSessionAction(
        newSession.accessToken,
        newSession.id,
      )

      if (!result.success) {
        throw new Error(result.error || 'Failed to set session')
      }

      // Invalidate the session query to refetch from cookie
      await client.invalidateQueries({ queryKey: ['session'] })
    },
    [client],
  )

  const logout = useCallback(
    async (redirectPath?: string) => {
      await clearSessionData(client)

      // Force string coercion with template literal to avoid client reference
      const path = `${redirectPath || ''}`
      await logoutAction(path)
    },
    [client],
  )

  const handleTokenRefresh = useCallback(() => {
    const accessToken = session?.accessToken
    if (!accessToken) {
      return
    }

    refreshToken(accessToken, {
      onSuccess: (data) => {
        const result = handleRefreshTokenResponse(data, userId)

        if (result.success) {
          openSession(result.session)
        }
      },
    })
  }, [session?.accessToken, refreshToken, userId, openSession])

  useInterval(handleTokenRefresh, TOKEN_REFRESH_INTERVAL)

  const unauthorized = async () => {
    toast.error(t('title'), {
      description: t('description'),
    })
    await logout()
  }

  const validateAPIResponse = async (
    response: ModulusAPIResponse<unknown> | null,
  ) => {
    const isUnauthorized = getIsResponseUnauthorized(response)

    if (isUnauthorized) {
      await unauthorized()
    }
  }

  // Refresh token on network
  // Listen for network changes
  useNetworkChange(handleTokenRefresh)

  return (
    <SessionContext.Provider
      value={{
        session,
        accessToken: session?.accessToken,
        isAuthenticated: !!session?.accessToken,
        openSession,
        logout,
        validateAPIResponse,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export default SessionProvider
